import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useTranslation } from "react-i18next"; // i18next hook
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

// CSS
import "../0PageStyles.css";
import "./registerPage.css";

// Components
import InputField from "../../Components/Other Components/inputField";
import Button from "../../Components/Other Components/button";
import Title from "../../Components/Other Components/Title";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

// Functions and hooks
import { Register, GoogleLoginUser } from "../../BackendAccess/UserHooks";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";

export default function RegisterPage() {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(false);

  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleRegister = async () => {
    if (password !== passwordConfirm) {
      showNotification(t("register.error.passwordMismatch"), "error");
      return;
    }
    setLoading(true);
    try {
      const [data, err] = await Register(
        username,
        password,
        email,
        firstName,
        lastName
      );
      if (err) {
        setError(err);
      } else {
        navigate("/verificationPage", { state: { email } });
      }
    } catch {
      setError({ err: t("register.error.server"), errCode: 500 });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setLoading(true);
    try {
      const [data, err] = await GoogleLoginUser(credentialResponse.credential);
      if (err) {
        setError(err);
      } else {
        localStorage.setItem("token", data.token);
        showNotification(t("register.success.google"), "success");
        navigate("/workspace/warehouses");
      }
    } catch {
      setError({ err: t("register.error.googleUnexpected"), errCode: 500 });
    } finally {
      setLoading(false);
    }
  };

  useErrorHandler(error, showNotification, setPageError);

  return (
    <PageWrapper>
      <PageLoader loading={loading} error={pageError}>
        <>
          <div className="page-content">
            <div className="spacer l" />
            <div className="container-bordered vertical register w-auto">
              <Title margin={false} textPosition="center">
                {t("register.title")}
              </Title>
              <InputField
                placeholder={t("register.placeholder.username")}
                label={t("register.label.username")}
                value={username}
                onChange={setUsername}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                labelPosition="top-left"
                mandatory
                description={t("register.desc.username")}
              />
              <InputField
                placeholder={t("register.placeholder.email")}
                label={t("register.label.email")}
                type="email"
                value={email}
                onChange={setEmail}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                labelPosition="top-left"
                mandatory
                description={t("register.desc.email")}
              />
              <InputField
                placeholder={t("register.placeholder.firstName")}
                label={t("register.label.firstName")}
                value={firstName}
                onChange={setFirstName}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                labelPosition="top-left"
                mandatory
              />
              <InputField
                placeholder={t("register.placeholder.lastName")}
                label={t("register.label.lastName")}
                value={lastName}
                onChange={setLastName}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                labelPosition="top-left"
                mandatory
              />
              <InputField
                placeholder={t("register.placeholder.password")}
                label={t("register.label.password")}
                type="password"
                value={password}
                onChange={setPassword}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                labelPosition="top-left"
                mandatory
              />
              <InputField
                placeholder={t("register.placeholder.confirmPassword")}
                label={t("register.label.confirmPassword")}
                type="password"
                value={passwordConfirm}
                onChange={setPasswordConfirm}
                inputMargins={["0rem", "0.7rem", "0.7rem", "0.7rem"]}
                labelPosition="top-left"
                mandatory
              />
              <MandatoryExplanation />

              <div className="btn-container">
                <Button
                  className="mt-3 mb-3"
                  btnStyle="btn-positive"
                  onClick={handleRegister}
                  acceptEnter
                >
                  {t("register.button")}
                </Button>
              </div>
              <p className="text-prompt">
                {t("register.prompt.already")}
                <span className="text-link" onClick={() => navigate("/login")}>
                  {t("register.prompt.login")}
                </span>
              </p>
              <GoogleLogin
                className="my-custom-google-btn"
                onSuccess={handleGoogleLogin}
                onError={() =>
                  showNotification(t("register.error.googleFail"), "error")
                }
                text="continue_with"
              />
            </div>
          </div>
        </>
      </PageLoader>
    </PageWrapper>
  );
}
